// ─── InventoryPage.jsx ────────────────────────────────────────
import { ClipboardList, Download, HardDrive, Search } from 'lucide-react';
import { useState, useEffect } from 'react';
import { deviceController } from '../../controllers/deviceController';
export const  DeviceInventoryPage = () => {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('deviceName');
  const [sortDir, setSortDir] = useState('asc');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    deviceController.getAll().then(r => {
      if (r.success) setDevices(r.data);
      setLoading(false);
    });
  }, []);

  const filtered = devices
    .filter(d => !searchQuery || d.deviceName?.toLowerCase().includes(searchQuery.toLowerCase()) || d.serialNumber?.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => {
      const av = a[sortField], bv = b[sortField];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      return sortDir === 'asc' ? av - bv : bv - av;
    });

  const handleSort = f => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } };

  const s = {
    page: { padding: 0 },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '16px' },
    titleWrap: { display: 'flex', alignItems: 'center', gap: '12px' },
    icon: { width: '44px', height: '44px', borderRadius: '12px', backgroundColor: '#64748b20', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    title: { fontSize: '26px', fontWeight: '700', color: '#1e293b', margin: 0 },
    subtitle: { fontSize: '13px', color: '#64748b', marginTop: '2px' },
    btnSecond: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 18px', backgroundColor: '#fff', color: '#1e293b', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
    statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px', marginBottom: '22px' },
    statCard: { backgroundColor: '#fff', padding: '18px', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
    statLabel: { fontSize: '12px', color: '#64748b', marginBottom: '6px' },
    statVal: { fontSize: '26px', fontWeight: '700', color: '#1e293b' },
    filterBar: { display: 'flex', gap: '10px', marginBottom: '18px' },
    searchWrap: { position: 'relative', minWidth: '260px' },
    searchIn: { width: '100%', padding: '9px 9px 9px 36px', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box' },
    searchIco: { position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' },
    card: { backgroundColor: '#fff', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', overflow: 'hidden' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { backgroundColor: '#f8fafc', padding: '12px 16px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#475569', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', whiteSpace: 'nowrap' },
    td: { padding: '12px 16px', borderBottom: '1px solid #f1f5f9', fontSize: '13px', color: '#1e293b' },
    badge: { display: 'inline-block', padding: '2px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' },
    progBar: { height: '4px', borderRadius: '2px', backgroundColor: '#e2e8f0', overflow: 'hidden', marginTop: '3px', width: '80px' },
  };

  const platformCounts = { Android: devices.filter(d => d.platform === 'Android').length, iOS: devices.filter(d => d.platform === 'iOS').length, Windows: devices.filter(d => d.platform === 'Windows').length };

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.titleWrap}>
          <div style={s.icon}><ClipboardList size={22} color="#64748b" /></div>
          <div><h1 style={s.title}>Device Inventory</h1><div style={s.subtitle}>Complete hardware inventory</div></div>
        </div>
        <button style={s.btnSecond}><Download size={15} /> Export CSV</button>
      </div>

      <div style={s.statsGrid}>
        {[['Total Devices', devices.length], ['Android', platformCounts.Android], ['iOS', platformCounts.iOS], ['Windows', platformCounts.Windows]].map(([l, v]) => (
          <div key={l} style={s.statCard}><div style={s.statLabel}>{l}</div><div style={s.statVal}>{v}</div></div>
        ))}
      </div>

      <div style={s.filterBar}>
        <div style={s.searchWrap}>
          <Search size={14} style={s.searchIco} />
          <input type="text" placeholder="Search inventory..." style={s.searchIn} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} />
        </div>
      </div>

      <div style={s.card}>
        {loading ? <div style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>Loading inventory...</div> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  {[['Device Name', 'deviceName'], ['Serial #', 'serialNumber'], ['Platform', 'platform'], ['Model', 'model'], ['Manufacturer', 'manufacturer'], ['Assigned To', 'assignedTo'], ['Storage', 'storageTotal'], ['Enrolled', 'enrollmentDate']].map(([label, field]) => (
                    <th key={field} style={s.th} onClick={() => handleSort(field)}>{label}{sortField === field ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map(dev => {
                  const storePct = dev.getStoragePercent();
                  return (
                    <tr key={dev.id}
                      onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f8fafc'}
                      onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}>
                      <td style={s.td}><div style={{ fontWeight: '500' }}>{dev.deviceName}</div></td>
                      <td style={s.td}><code style={{ fontSize: '11px', color: '#64748b' }}>{dev.serialNumber}</code></td>
                      <td style={s.td}><span style={{ ...s.badge, backgroundColor: `${dev.getPlatformColor()}15`, color: dev.getPlatformColor() }}>{dev.platform}</span></td>
                      <td style={s.td}>{dev.model}</td>
                      <td style={s.td}>{dev.manufacturer || '—'}</td>
                      <td style={s.td}>{dev.assignedTo || '—'}</td>
                      <td style={s.td}>
                        <div style={{ fontSize: '11px' }}>{dev.storageUsed}/{dev.storageTotal}GB</div>
                        <div style={s.progBar}><div style={{ height: '100%', width: `${storePct}%`, backgroundColor: storePct > 85 ? '#ef4444' : '#3b82f6', borderRadius: '2px' }} /></div>
                      </td>
                      <td style={s.td}>{new Date(dev.enrollmentDate).toLocaleDateString('en-IN')}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
export default DeviceInventoryPage;